import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, Card, Chip, Skeleton, Typography } from '@mui/material';
import NoDataFound from '../../../components/NodataIndication/NoDataFound';
import ToolTip from '../../../components/ToolTip/ToolTip';
import { formatCurrency, getDeptColor, getDeptShortName } from '../utils/deptDisplay';

// Each department's own highest income day of the month so far, in the same style as the Department Share donut: a
// bordered card, centred title, one rounded chip per department with its best day and amount. Biggest day first, so the
// top chip is the same day as the Biggest revenue day card; a department with no revenue yet is listed last.
// `highest` comes from highestDayByDepartment() (utils/incomeInsights.js).
//
//  - roomy (default): two-line chips (name + amount, then the day and the share of that department's month);
//  - dense: ONE line per department, used when the column beside the table is too short for the roomy version;
//  - the card can be stretched by its parent (`sx`): the chips then spread evenly over the height it is given, each chip
//    staying a sensible size and centred in its slot, so the bottom of the column lines up with the bottom of the table.
export default function HighestDayByDepartment({ highest, loading, dense = false, sx }) {
  const sorted = useMemo(() => [...(highest || [])].sort((a, b) => b.amount - a.amount), [highest]);
  const hasRevenue = sorted.some((d) => d.amount > 0);
  const when = (d) => (d.date ? `${dayjs(d.date).format('ddd, D MMM')} · ${d.shareOfDeptMonth}% of its month` : 'No revenue yet this month');
  const dot = (d) => <Box sx={{ width: 14, height: 14, borderRadius: '50%', flexShrink: 0, backgroundColor: getDeptColor(d.deptId) }} />;
  const name = (d) => (
    <ToolTip
      expand={d.deptName}
      content={
        <Typography sx={{ fontWeight: 500, fontSize: 14, maxWidth: 110, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {getDeptShortName(d.deptId, d.deptName)}
        </Typography>
      }
    />
  );

  return (
    <Card sx={{ p: dense ? 2 : 3, borderRadius: '16px', border: '1px solid #d698cfff', display: 'flex', flexDirection: 'column', ...sx }}>
      <Typography variant={dense ? 'subtitle1' : 'h6'} fontWeight={600} textAlign="center" lineHeight={1.3}>
        Highest Income Day by Department
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={dense ? 1.25 : 2}>
        Each department&apos;s best collection day this month
      </Typography>

      {loading ? (
        <Skeleton variant="rectangular" height={dense ? 160 : 200} sx={{ borderRadius: '12px' }} />
      ) : !hasRevenue ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={dense ? 100 : 120}>
          <NoDataFound message="No revenue recorded yet this month" />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${dense ? 330 : 290}px, 1fr))`, gridAutoRows: '1fr', gap: dense ? 0.5 : 1 }}>
          {sorted.map((d) => (
            <Box key={d.deptId} sx={{ display: 'flex', alignItems: 'center' }}>
              {dense ? (
                <Box sx={{ width: '100%', maxHeight: 44, display: 'grid', gridTemplateColumns: '14px minmax(56px, 1fr) auto auto', alignItems: 'center', columnGap: 1, px: 1.25, py: 0.25, borderRadius: '10px', backgroundColor: '#f9f9f9' }}>
                  {dot(d)}
                  {name(d)}
                  <Typography variant="caption" color="text.secondary" noWrap>{when(d)}</Typography>
                  <Chip size="small" sx={{ fontWeight: 600 }} label={formatCurrency(d.amount)} />
                </Box>
              ) : (
                <Box sx={{ width: '100%', maxHeight: 96, px: 1.5, py: 1, borderRadius: '12px', backgroundColor: '#f9f9f9' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {dot(d)}
                    {name(d)}
                    <Chip sx={{ ml: 'auto', fontWeight: 600 }} size="small" label={formatCurrency(d.amount)} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: '22px', mt: 0.25 }}>{when(d)}</Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Card>
  );
}
